import ReactPlayer from "react-player";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { isMobile } from "react-device-detect";
import styles from "./VideoTask.module.scss";
import { StatusType, TaskType, TranscriptType } from "App.types";
import { SentenceTaskProps } from "../SentenceTask.type";
import sentStyles from "../SentenceTask.module.scss";
import TaskPanel from "Components/TaskPanel";
import VideoPlayer from "Components/SentenceTask/VideoPlayer";
import { SkipTags, STATIC_URL } from "App.constants";
import { encodeS3URI } from "App.helpers";
import { useQuery } from "@tanstack/react-query";
import API from "Api";
import { groupBy, orderBy } from "lodash";

import "./styles.css";
import { PlaySquareOutlined } from "@ant-design/icons";

const VideoTask: FC<SentenceTaskProps> = ({
  autoPlay = true,
  onNext,
  sentence: { id, medias, media, markers, storyId, mediaStory, mediaStoryId, transcripts },
  onTaskComplete,
  showGrammar,
  lesson,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [duration, setDuration] = useState<number>();

  const { data: storyTrans = [] } = useQuery({
    staleTime: Infinity,
    queryKey: ["transcripts", mediaStoryId],
    enabled: !!mediaStoryId,
    queryFn: () => (mediaStoryId ? API.transcriptions.getByStoryId(mediaStoryId) : undefined),
    select: (data) => orderBy(data, "id"),
  });

  const playerRef = useRef<ReactPlayer>(null);

  useEffect(() => {
    setStatus(StatusType.Empty);
  }, [id]);

  const mediaUrl = useMemo(
    () => media && !/^\d+$/.test(media) && (media.includes("http") ? media : `${STATIC_URL}/media/${media}`),
    [media],
  );
  const nonDraftMedias = useMemo(() => medias.filter((m) => m.meta !== "draft"), [medias]);

  const oneMediaUrl = useMemo(
    () => (nonDraftMedias.length === 1 ? `${STATIC_URL}/media/${nonDraftMedias[0].url}` : undefined),
    [nonDraftMedias],
  );
  const url = mediaUrl || oneMediaUrl;

  const onClick = () => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      // if (activeIdx === medias.length - 1) setActiveIdx(0);
      setIsPlaying(true);
    }
  };

  const groupedTrans = useMemo<{ [key: number]: TranscriptType[] }>(
    () => groupBy(mediaStoryId ? storyTrans : transcripts, "sent"),
    [transcripts, mediaStoryId, storyTrans],
  );

  const showSubtitles = markers.includes("subtitles");

  useEffect(() => {
    const player = playerRef.current?.getInternalPlayer();

    if (player && duration && storyTrans.length && showSubtitles) {
      for (const track of player.textTracks) {
        track.mode = "hidden";
      }
      const textTrack = player.addTextTrack("captions", "English", "en");
      textTrack.mode = "showing";

      storyTrans.forEach((t) => {
        const trRow = groupedTrans[t.sent ?? 0];
        const text = trRow
          ?.map((tr) => (tr.start === t.start && tr.text.trim() && !SkipTags.includes(tr.text.trim()) ? `<u>${tr.text}</u>` : tr.text))
          .join("")
          .trim();

        textTrack.addCue(new VTTCue(t.start, t.end, ` ${text} `));
      });
    } else if (player && duration && transcripts.length && showSubtitles) {
      for (const track of player.textTracks) {
        track.mode = "hidden";
      }
      const textTrack = player.addTextTrack("captions", "English", "en");
      textTrack.mode = "showing";

      transcripts.forEach((t) => {
        textTrack.addCue(new VTTCue(t.start, t.end, `<u>${t.text}</u>`));
      });
    } else if (player?.textTracks?.length) {
      player.textTracks[0].mode = "disabled";
    }
  }, [duration, transcripts, groupedTrans, showSubtitles, storyTrans]);

  const isShortVideo = !!duration && duration < 15;

  const onComplete = useCallback(() => {
    setIsPlaying(false);
    setStatus(StatusType.Completed);

    if (status !== StatusType.Completed) {
      onTaskComplete();
    }
  }, [status, onTaskComplete]);

  return (
    <div className={cx(sentStyles.sentenceTask, styles.videoTask, { [styles.videoTask_mobile]: isMobile })}>
      <div className={styles.player} onClick={onClick}>
        {url && (
          <>
            <ReactPlayer
              ref={playerRef}
              onDuration={setDuration}
              onStart={() => setIsPlaying(true)}
              playing={isPlaying}
              playsinline={true}
              controls={!isShortVideo}
              onError={onComplete}
              onEnded={onComplete}
              width={isMobile ? "100%" : ""}
              height={isMobile ? "" : "100%"}
              url={url}
            />
            {!isPlaying && isShortVideo && <PlaySquareOutlined className={styles.btnPlay} />}
          </>
        )}

        {mediaStory && (
          <>
            <ReactPlayer
              onDuration={setDuration}
              ref={playerRef}
              height={""}
              width={"100%"}
              onError={onComplete}
              onEnded={onComplete}
              playsinline={true}
              controls={!isShortVideo}
              playing={isPlaying}
              url={`${STATIC_URL}/stories/${encodeS3URI(mediaStory?.title)}/video.mp4`}
            />
            {!isPlaying && isShortVideo && <PlaySquareOutlined className={styles.btnPlay} />}
          </>
        )}

        {nonDraftMedias.length > 1 && (
          <VideoPlayer
            transcripts={transcripts}
            defaultHeight={480}
            showCaptions={markers.includes("subtitles")}
            medias={nonDraftMedias}
            autoPlay={autoPlay}
            onFinish={onComplete}
          />
        )}

        {/*<Button type={"link"} size={"large"} onClick={onClose} className={styles.btnClose} icon={<CloseOutlined size={28} />} />*/}
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.Video}
        sentId={id}
        storyId={storyId}
        showGrammar={showGrammar}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
      />
    </div>
  );
};

export default VideoTask;
